import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Select from "react-select";
import {
  Button,
  Form,
  Table,
  Modal,
  Spinner,
  Row,
  Col,
  InputGroup,
  Dropdown,
} from "react-bootstrap";
import "./../../assets/scss/reports/cashbook.scss";
import ExpensesSelector from "../utils/ExpensesSelector";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { amountTypes, formatDate } from "../../utils/helpers";
import DateTime from "react-datetime";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import ConfirmDialog from "../ConfirmDialogue";
import { el } from "date-fns/locale";
import PageHeader from "../PageHeader";
import {
  ChartPieSliceTitleIcon,
  EditIcon,
  MoneyDollarIcon,
  SummaryChartIconDown,
  TrashIcon,
} from "../Icons";
import { isEmpty } from "lodash";
import { useScrollTop } from "../../utils/hooks";
import currency from "currency.js";
import AddAccounts from "../modals/AddAccounts";
import CachedIcon from "mdi-react/CachedIcon";
import PlayIcon from "mdi-react/PlayIcon";
import PauseIcon from "mdi-react/PauseIcon";

// 12, 4, 2, 1
const cycleOptions = [
  { label: "Monthly", value: "Monthly", divideBy: 12, humanText: "per month" },
  {
    label: "Quaterly",
    value: "Quaterly",
    divideBy: 4,
    humanText: "per quarter",
  },
  {
    label: "Bi-annually",
    value: "Bi-anually",
    divideBy: 2,
    humanText: "per half year",
  },
  { label: "Annually", value: "Annually", divideBy: 1, humanText: "per year" },
];

const usageOptions = [
  { value: "All Fixed Assets", label: "All Fixed Assets" },
  //  { value: "Category", label: "Category" }, // DEPRECATED
  { value: "Account Description", label: "Account Description" },
  { value: "Items", label: "Item" },
];

export default function Depreciation() {
  useScrollTop();
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [showCreateNewAccountModal, setShowCreateNewAccountModal] = useState(
    false
  );
  const [
    showCreateNewExpenseAccountModal,
    setShowCreateNewExpenseAccountModal,
  ] = useState(false);

  const fetchItemsAndCategories = async () => {
    const res = await fetch(`${backendUrl}/api/items?getItemFrom=Equipment`, {
      credentials: "include",
    });
    return res.json();
  };

  const fetchAccountDescriptions = async () => {
    const res = await fetch(
      `${backendUrl}/api/journal?Type=FIXED ASSET,NON-CURRENT ASSETS`,
      {
        credentials: "include",
      }
    );
    return res.json();
  };

  const fetchDepreciations = async () => {
    const res = await fetch(`${backendUrl}/api/depreciations`, {
      credentials: "include",
    });
    return res.json();
  };

  const createDepreciation = async data => {
    let response = await fetch(
      `${backendUrl}/api/depreciations/create-or-update`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const res = await response.json();
    return res;
  };

  const updateDepreciation = async ({ id, data }) => {
    const res = await fetch(
      `${backendUrl}/api/depreciations/create-or-update`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, ...data }),
        credentials: "include",
      }
    );
    return res.json();
  };

  const deleteDepreciation = async id => {
    await fetch(`${backendUrl}/api/depreciations/${id}`, { method: "DELETE" });
  };

  // Queries
  const { data: itemsData, refetch: refetchItems } = useQuery(
    "items",
    fetchItemsAndCategories
  );

  const {
    data: depreciations = [],
    isLoading,
    refetch: refetchDepreciations,
  } = useQuery("depreciations", fetchDepreciations);

  const { data: journalData, refetch: refetchJournal } = useQuery(
    "journal",
    fetchAccountDescriptions
  );

  const items = itemsData?.data?.items || [];
  const categories = itemsData?.data?.category || [];
  const accountDescriptions = journalData?.data?.journal || [];

  // Options for selects
  const categoryOptions = categories?.map(cat => ({
    value: cat.Cat_Name,
    label: cat.Cat_Name,
  }));
  const itemOptions = items?.map(item => ({
    value: item.Bar_Code,
    label: `${item.Bar_Code} - ${item.Item_Name}`,
  }));

  const accountDescriptionOptions = accountDescriptions?.map(acc => ({
    ...acc,
    value: acc.AccountID,
    label: `${acc.AccountID} - ${acc.Description}`,
  }));

  const foundDepreciation = useMemo(
    () => (depreciations ? depreciations.find(d => d.id === editId) : null),
    [depreciations, editId]
  );

  const getInitialValue = ({ editId, depreciations = [] }) => {
    if (editId && !isEmpty(depreciations) && foundDepreciation) {
      return {
        title: foundDepreciation.title,
        cycle: foundDepreciation.cycle || "Monthly",
        type: foundDepreciation.type || "Percentage",
        fixedAmount: foundDepreciation.fixedAmount || 0,
        percentage: foundDepreciation.percentage || null,
        depreciationPercentage:
          foundDepreciation.depreciationPercentage || null,
        Post_Date: foundDepreciation.Post_Date
          ? new Date(foundDepreciation.Post_Date).toISOString().slice(0, 10)
          : null,
        // Start_Date: null, // THIS SHOULD BE PER ITEM
        accumulatedAccountID: foundDepreciation.accumulatedAccountID || "",
        accumulatedAccountDescription:
          foundDepreciation.accumulatedAccountDescription || "",
        accumulatedAccountType: foundDepreciation.accumulatedAccountType || "",
        usage: foundDepreciation.usage || "",
        usageItems: foundDepreciation.usageItems
          ? JSON.parse(foundDepreciation.usageItems)
          : [],
        expenseAccountID: foundDepreciation.expenseAccountID || "",
        expenseAccountDescription:
          foundDepreciation.expenseAccountDescription || "",
        expenseAccountType: foundDepreciation.expenseAccountType || "",
      };
    } else {
      return {
        title: "",
        cycle: "Monthly",
        type: "Percentage",
        fixedAmount: 0,
        percentage: null,
        depreciationPercentage: null,
        Post_Date: new Date().toISOString().slice(0, 10),
        // Start_Date: null, // THIS SHOULD BE PER ITEM
        accumulatedAccountID: "",
        accumulatedAccountDescription: "",
        accumulatedAccountType: "",
        usage: "",
        usageItems: [],
        // ---------------------------------------------------------------------
        expenseAccountID: "",
        expenseAccountDescription: "",
        expenseAccountType: "",
      };
    }
  };

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValue({
      editId,
      depreciations,
    }),
    onSubmit: (values, { resetForm }) => {
      if (isEmpty(values.title)) return toast.error("Title is required");

      // DEPRECATED PER ITEM NOW
      // if (isEmpty(values.Start_Date)) {
      //   return toast.error("Start Date is required");
      // }

      if (!values.accumulatedAccountID) {
        return toast.error("Accumulated Account is required");
      }
      if (!values.expenseAccountID) {
        return toast.error("Expense Account is required");
      }

      if (editId) {
        updateMutation.mutate({ id: editId, data: values });
        setEditId(null);
      } else {
        if (!isEmpty(depreciations)) {
          if (values.usage === "All Fixed Assets") {
            const foundEntry = depreciations?.find(
              depreciation => depreciation.usage === "All Fixed Assets"
            );
            if (foundEntry)
              return toast.error("'All Fixed Assets' has already been set");
          } else if (values.usage === "Category") {
            // const foundEntry = depreciations?.find(
            //   depreciation => depreciation.usage === "Category"
            // );
          } else if (values.usage === "Items") {
            // const foundEntry = depreciations?.find(
            //   depreciation => depreciation.usage === "Items"
            // );
          } else if (values.usage === "Account Description") {
            const foundEntries = depreciations?.filter(
              depreciation => depreciation.usage === "Account Description"
            );

            const foundEntry = foundEntries?.find(depreciation =>
              JSON.parse(depreciation.usageItems).includes(values.usageItems[0])
            );

            if (foundEntry) {
              return toast.error(
                `'${values.usageItems[0]}' has already been set, Select another`
              );
            }
          }
        }

        createMutation.mutate(values);
      }
    },
  });

  // Handlers
  const handleEdit = id => {
    setEditId(id);
    setShowModal(true);
  };

  const handleDelete = async id => {
    if (
      await ConfirmDialog({
        title: "Delete?",
        description: "Are you sure you want to delete?",
        type: "danger",
      })
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleShowModal = () => {
    setEditId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditId(null);
    setShowModal(false);
    formik.resetForm();
  };

  // Mutations
  const createMutation = useMutation(createDepreciation, {
    onSuccess: () => {
      queryClient.invalidateQueries("depreciations");
      formik.resetForm();
      setShowModal(false);
    },
    onError: error => {
      console.log(error);
      toast.error?.(`Failed to create depreciation: ${error?.message}`);
    },
  });

  const updateMutation = useMutation(updateDepreciation, {
    onSuccess: () => {
      queryClient.invalidateQueries("depreciations");
      formik.resetForm();
      setShowModal(false);
    },
    onError: error => {
      toast?.error?.(`Failed to update depreciation: ${error?.message}`);
    },
  });

  const deleteMutation = useMutation(deleteDepreciation, {
    onSuccess: () => queryClient.invalidateQueries("depreciations"),
    onError: error => {
      toast?.error?.(`Failed to delete depreciation: ${error?.message}`);
    },
  });

  // GA ===============================================================
  const [showJournalPopover, setShowJournalPopover] = useState(false);
  const handleSelectedAccount = account => {
    formik.setFieldValue("accumulatedAccountID", account?.AccountID);
    formik.setFieldValue("accumulatedAccountDescription", account?.Description);
    formik.setFieldValue("accumulatedAccountType", account?.Type);
    setShowJournalPopover(false);
  };

  // GA ===============================================================
  // EXPENSE SECOND LEG
  const [showJournalExpensePopover, setShowJournalExpensePopover] = useState(
    false
  );
  const handleSelectedExpenseAccount = account => {
    formik.setFieldValue("expenseAccountID", account?.AccountID);
    formik.setFieldValue("expenseAccountDescription", account?.Description);
    formik.setFieldValue("expenseAccountType", account?.Type);
    setShowJournalExpensePopover(false);
  };

  const calcutePercentage = (depreciationPercentage = 0, cycle) => {
    const divideBy = cycle
      ? cycleOptions.find(el => el.value === cycle)?.divideBy
      : 1;

    const percentage = currency(depreciationPercentage, {
      precision: 2,
    }).divide(divideBy).value;

    formik.setFieldValue("percentage", percentage || 0);
    console.log({ depreciationPercentage, divideBy, percentage });
  };

  const handleDepreciationPercentageChange = e => {
    const depreciationPercentage = e.target.value;
    formik.setFieldValue("depreciationPercentage", depreciationPercentage);
    calcutePercentage(depreciationPercentage, formik.values.cycle);
  };

  const handleCycleChange = selected => {
    console.log(selected);
    formik.setFieldValue("cycle", selected.value);
    calcutePercentage(formik.values.depreciationPercentage, selected.value);
  };

  const handleSelectedAccountDescription = selected => {
    formik.setFieldValue("usageItems", [selected?.value]);

    const subAccount = accountDescriptions.find(
      acc => acc.subAccountID === selected.value
    );

    if (subAccount) {
      formik.setFieldValue("accumulatedAccountID", subAccount?.AccountID);
      formik.setFieldValue(
        "accumulatedAccountDescription",
        subAccount?.Description
      );
      formik.setFieldValue("accumulatedAccountType", subAccount?.Type);
    } else {
      formik.setFieldValue("accumulatedAccountID", "");
      formik.setFieldValue("accumulatedAccountDescription", "");
      formik.setFieldValue("accumulatedAccountType", "");

      toast.info("No associated Accumulated Depreciation Account found");
    }
  };

  return (
    <>
      <main className="inner-page-outlet">
        <PageHeader name="Depreciation Setup" icon={<SummaryChartIconDown />} />
        <main className="cash-book">
          <div className="content">
            <div className="content-main">
              <div className="content-body">
                <header>
                  <h1>
                    <Button variant="" onClick={() => refetchDepreciations()}>
                      <CachedIcon />
                    </Button>{" "}
                  </h1>

                  <div>
                    <Button variant="primary" onClick={handleShowModal}>
                      + Add New
                    </Button>
                  </div>
                </header>

                <div className="px-4">
                  {isLoading ? (
                    <div>
                      <Spinner animation="border" size="sm" /> Loading...
                    </div>
                  ) : (
                    <Table
                      responsive
                      borderless
                      striped
                      className="product-table"
                    >
                      <thead>
                        <tr>
                          <th />
                          <th />
                          <th>Title</th> <th>By</th> <th>{"  "}</th>
                          <th>Cycle</th>
                          <th>Type</th>
                          {/*   <th>Fixed Amount</th> */}
                          <th>Percentage</th>
                          <th>Post Date</th>
                          {/*    <th>Start Date</th> */}
                          <th className="text-nowrap">Accum. Account</th>
                          <th className="text-nowrap">Expense Account</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depreciations.map(d => (
                          <tr key={d.id}>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant=""
                                  className="bg-white border-0"
                                  bsPrefix="print more"
                                >
                                  <DotsVerticalIcon />
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  popperConfig={{
                                    strategy: "fixed",
                                  }}
                                  renderOnMount={true}
                                  className="dropdown-with-icons"
                                >
                                  <Dropdown.Item
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => handleEdit(d.id)}
                                    className="me-2"
                                  >
                                    <EditIcon />
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(d.id)}
                                    disabled={deleteMutation.isLoading}
                                  >
                                    <TrashIcon />
                                    {deleteMutation.isLoading ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      "Delete"
                                    )}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                            <td>
                              <PauseDepreciation
                                depreciationId={d.id}
                                isPaused={d.isPaused}
                              />
                            </td>
                            <td>{d.title}</td>{" "}
                            <td className="text-nowrap">{d.usage}</td>
                            <td>
                              {(d.usage === "Category"
                                ? categoryOptions
                                : d.usage === "Items"
                                ? itemOptions
                                : d.usage === "Account Description"
                                ? accountDescriptionOptions
                                : []
                              )
                                .filter(o =>
                                  JSON.parse(d.usageItems)?.includes(o.value)
                                )
                                .map(o => (
                                  <span key={o.value} className="text-nowrap">
                                    {o.label}
                                    <br />
                                  </span>
                                ))}
                            </td>
                            <td>{d.cycle}</td>
                            <td>{d.type}</td>
                            {/*   <td>{d.fixedAmount}</td> */}
                            <td>{d.percentage} %</td>
                            <td className="text-nowrap">
                              {formatDate(d.Post_Date)}
                            </td>
                            {/*  <td className="text-nowrap">
                              {formatDate(d.Start_Date)}
                            </td> */}
                            <td className="text-nowrap">
                              {d.accumulatedAccountID} /{" "}
                              {d.accumulatedAccountDescription} /{" "}
                              {d.accumulatedAccountType}
                            </td>
                            <td className="text-nowrap">
                              {d.expenseAccountID} /{" "}
                              {d.expenseAccountDescription} /{" "}
                              {d.expenseAccountType}
                            </td>
                          </tr>
                        ))}
                        {depreciations.length === 0 && (
                          <tr>
                            <td colSpan={12}>No records found.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </div>{" "}
            </div>{" "}
          </div>
        </main>
      </main>

      {showModal && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          size="md"
          centered
          animation={false}
          enforceFocus={false}
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editId ? "Edit Depreciation" : "Add Depreciation"}{" "}
              <Button
                variant=""
                size="sm"
                onClick={() => {
                  refetchItems();
                  refetchJournal();
                }}
              >
                <CachedIcon />
              </Button>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={formik.handleSubmit}>
              <Form.Group className="mb-3 pb-2">
                <Form.Label className="mb-1">Title</Form.Label>
                <Form.Control
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                />{" "}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>By</Form.Label>
                <Select
                  options={usageOptions}
                  onChange={option => {
                    formik.setFieldValue("usage", option.value);
                    formik.setFieldValue("usageItems", []);
                  }}
                  value={usageOptions.find(
                    o => o.value === formik.values.usage
                  )}
                  isDisabled={Boolean(editId !== null)}
                />
              </Form.Group>

              {formik.values.usage === "Category" && (
                <Form.Group className="mb-3">
                  <Form.Label>Categories</Form.Label>
                  <Select
                    isMulti
                    options={categoryOptions}
                    onChange={selected =>
                      formik.setFieldValue(
                        "usageItems",
                        selected ? selected.map(s => s.value) : []
                      )
                    }
                    value={categoryOptions.filter(o =>
                      formik.values.usageItems.includes(o.value)
                    )}
                  />
                </Form.Group>
              )}

              {/* CAN PICK ONLY ONE */}
              {formik.values.usage === "Account Description" && (
                <Form.Group className="mb-3">
                  <Form.Label>Account Description</Form.Label>
                  <Select
                    options={accountDescriptionOptions}
                    // CAN PICK ONE
                    onChange={handleSelectedAccountDescription}
                    value={accountDescriptionOptions.filter(o =>
                      formik.values.usageItems.includes(o.value)
                    )}
                    //  isDisabled={Boolean(editId !== null)}
                  />
                </Form.Group>
              )}

              {formik.values.usage === "Items" && (
                <Form.Group className="mb-3">
                  <Form.Label>Items</Form.Label>
                  <Select
                    isMulti
                    options={itemOptions}
                    onChange={selected => {
                      if (selected) {
                        if (Number(selected.Quantity) < 0) {
                          return toast.error("Not enough stock");
                        }

                        if (Number(selected.UnitCost) <= 0) {
                          return toast.error("Item has no Unit Cost");
                        }
                      }

                      formik.setFieldValue(
                        "usageItems",
                        selected ? selected.map(s => s.value) : []
                      );
                    }}
                    value={itemOptions.filter(o =>
                      formik.values.usageItems.includes(o.value)
                    )}
                  />
                </Form.Group>
              )}

              <hr />
              <Row>
                <Col>
                  <Form.Group className="mb-3 pb-2">
                    <Form.Label className="mb-1">
                      Depreciation Percentage (%)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="depreciationPercentage"
                      value={formik.values.depreciationPercentage}
                      onChange={handleDepreciationPercentageChange}
                      placeholder="0.0"
                    />{" "}
                  </Form.Group>

                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Cycle</Form.Label>
                        <Select
                          classNamePrefix={"form-select"}
                          options={cycleOptions}
                          value={cycleOptions.find(
                            el => el.value === formik.values.cycle
                          )}
                          onChange={handleCycleChange}
                          isDisabled={true}
                        />
                        {formik.touched.cycle && !!formik.errors.cycle ? (
                          <span className="text-danger mt-2">
                            {formik.errors.cycle}
                          </span>
                        ) : null}
                      </Form.Group>{" "}
                    </Col>

                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Type</Form.Label>
                        <Select
                          classNamePrefix={"form-select"}
                          options={amountTypes}
                          value={amountTypes?.find(
                            el => el.value === formik.values?.type
                          )}
                          onChange={selected =>
                            formik.setFieldValue("type", selected.value)
                          }
                          isDisabled={true}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {formik.values?.type === "Percentage" ? (
                    <Row>
                      <Col>
                        <Form.Group className="mb-3 pb-2">
                          <Form.Label className="mb-1">
                            Percentage (%){" "}
                            {
                              cycleOptions.find(
                                el => el.value === formik.values.cycle
                              )?.humanText
                            }{" "}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="percentage"
                            value={formik.values.percentage}
                            onChange={formik.handleChange}
                            readOnly
                            placeholder="0.0"
                          />{" "}
                        </Form.Group>
                      </Col>
                    </Row>
                  ) : formik.values?.type === "Fixed" ? (
                    <Form.Group className="mb-3 pb-2">
                      <Form.Label className="mb-1">Fixed Amount</Form.Label>
                      <CurrencyCustomInput
                        name="fixedAmount"
                        placeholder="0.00"
                        value={formik.values.fixedAmount}
                        onValueChange={(value, name) => {
                          formik.setFieldValue(name, value);
                        }}
                        readOnly
                      />{" "}
                    </Form.Group>
                  ) : null}

                  <hr />
                  <h2 className="mb-1 pb-1 h5">Account Postings</h2>
                  <Form.Group className="mb-3">
                    <Form.Label>Accumulated Depreciation</Form.Label>
                    <div className="d-flex gap-3">
                      <InputGroup hasValidation>
                        <Form.Control
                          placeholder="Select Account"
                          value={`${formik.values?.accumulatedAccountID ||
                            "..."} / ${formik.values
                            ?.accumulatedAccountDescription || "..."} / ${formik
                            .values?.accumulatedAccountType || "..."}`}
                          onChange={() => {}}
                          readOnly
                          isInvalid={
                            formik.touched.accumulatedAccountID &&
                            !!formik.errors.accumulatedAccountID
                          }
                        />

                        <Popover
                          isOpen={showJournalPopover}
                          onClickOutside={() => setShowJournalPopover(false)}
                          content={() => (
                            <ExpensesSelector
                              handleSelectedExpense={handleSelectedAccount}
                              usage={"chart of accounts"}
                              Type="NON-CURRENT ASSETS"
                            />
                          )}
                          position="bottom"
                        >
                          <InputGroup.Text
                            onClick={() => setShowJournalPopover(true)}
                            className="p-cursor"
                          >
                            <MagnifyIcon />
                          </InputGroup.Text>
                        </Popover>
                      </InputGroup>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setShowCreateNewAccountModal(true)}
                        className="text-nowrap"
                      >
                        + Create New
                      </Button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Expense</Form.Label>
                    <div className="d-flex gap-3">
                      <InputGroup hasValidation>
                        <Form.Control
                          placeholder="Select Account"
                          value={`${formik.values?.expenseAccountID ||
                            "..."} / ${formik.values
                            ?.expenseAccountDescription || "..."} / ${formik
                            .values?.expenseAccountType || "..."}`}
                          onChange={() => {}}
                          readOnly
                          isInvalid={
                            formik.touched.expenseAccountID &&
                            !!formik.errors.expenseAccountID
                          }
                        />

                        <Popover
                          isOpen={showJournalExpensePopover}
                          onClickOutside={() =>
                            setShowJournalExpensePopover(false)
                          }
                          content={() => (
                            <ExpensesSelector
                              handleSelectedExpense={
                                handleSelectedExpenseAccount
                              }
                              usage={"chart of accounts"}
                              Type="EXPENSES"
                            />
                          )}
                          position="bottom"
                        >
                          <InputGroup.Text
                            onClick={() => setShowJournalExpensePopover(true)}
                            className="p-cursor"
                          >
                            <MagnifyIcon />
                          </InputGroup.Text>
                        </Popover>
                      </InputGroup>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() =>
                          setShowCreateNewExpenseAccountModal(true)
                        }
                        className="text-nowrap"
                      >
                        + Create New
                      </Button>
                    </div>
                  </Form.Group>

                  <>
                    <hr />
                    {/*  <Form.Group className="mb-3">
                      <>
                        {editId && foundDepreciation ? (
                          <Form.Label>
                            Old Start Date:{" "}
                            {foundDepreciation.Start_Date
                              ? new Date(foundDepreciation.Start_Date)
                                  .toISOString()
                                  .slice(0, 10)
                              : null}
                          </Form.Label>
                        ) : null}
                        <br />
                      </>

                      <br />
                      {editId ? (
                        <>
                          <Form.Label>New Start Date</Form.Label>
                          <br />
                          <span className="text-danger">
                            Be careful, This will change the projection
                          </span>
                        </>
                      ) : (
                        <>
                          <Form.Label>Start Date</Form.Label>
                          <br />
                          <span>
                            (For existing Assets. <br /> New Assets will start
                            from the date the Asset Unit Cost was added)
                          </span>
                        </>
                      )}
                      <br />

                      <DateTime
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        dateFormat="MMM DD, YYYY"
                        inputProps={{
                          className: `date-input form-control`,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={formik.values.Start_Date}
                        key={formik.values.Start_Date}
                        onChange={date => {
                          formik.setFieldValue("Start_Date", date, true);
                        }}
                      />
                    </Form.Group> */}
                    <Form.Group className="mb-3">
                      <Form.Label>Post Date</Form.Label>
                      <DateTime
                        timeFormat={false}
                        closeOnSelect={true}
                        closeOnClickOutside={true}
                        dateFormat="MMM DD, YYYY"
                        inputProps={{
                          className: `date-input form-control`,
                          placeholder: "Select date",
                          readOnly: true,
                        }}
                        value={formik.values.Post_Date}
                        key={formik.values.Post_Date}
                        onChange={date => {
                          formik.setFieldValue("Post_Date", date, true);
                        }}
                      />
                    </Form.Group>
                  </>
                </Col>
              </Row>

              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    createMutation.isLoading || updateMutation.isLoading
                  }
                >
                  {createMutation.isLoading || updateMutation.isLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : editId ? (
                    "Update"
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      {showCreateNewAccountModal && (
        <AddAccounts
          show={showCreateNewAccountModal}
          onHide={() => setShowCreateNewAccountModal(false)}
          type={"NON-CURRENT ASSETS"}
          accountCreated={handleSelectedAccount}
        />
      )}

      {showCreateNewExpenseAccountModal && (
        <AddAccounts
          show={showCreateNewExpenseAccountModal}
          onHide={() => setShowCreateNewExpenseAccountModal(false)}
          type={"EXPENSES"}
          accountCreated={handleSelectedExpenseAccount}
        />
      )}
    </>
  );
}

function PauseDepreciation({ depreciationId, isPaused }) {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();

  const pauseDepreciation = async id => {
    const response = await fetch(
      `${backendUrl}/api/depreciations/pause-depreciation/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ isPaused: !isPaused }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to pause depreciation");
    }

    return response.json();
  };

  const pauseMutation = useMutation(pauseDepreciation, {
    onSuccess: () => {
      toast.success(
        `Depreciation ${isPaused ? "resumed" : "paused"} successfully`
      );
      queryClient.invalidateQueries("depreciations");
    },
    onError: error => {
      console.error(
        `Error ${isPaused ? "resuming" : "pausing"} depreciation:`,
        error
      );
      toast.error(
        `Error ${isPaused ? "resuming" : "pausing"} depreciation: ${
          error.message
        }`
      );
    },
  });

  const icon = pauseMutation.isLoading ? (
    <Spinner animation="border" size="sm" />
  ) : isPaused ? (
    <PlayIcon />
  ) : (
    <PauseIcon />
  );

  return (
    <Button
      onClick={() => pauseMutation.mutate(depreciationId)}
      disabled={pauseMutation.isLoading}
      size="sm"
      variant="light-blue"
    >
      <span className="d-flex gap-3 text-nowrap align-items-center">
        {isPaused ? <span>{icon} Resume</span> : <span>{icon} Pause</span>}
      </span>
    </Button>
  );
}
